import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, InputAdornment, MenuItem, Snackbar, Stack, Switch, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { CheckCircle, NetworkCheck, RadioButtonUnchecked, Send, Visibility, VisibilityOff } from '@mui/icons-material';
import FormDrawer from '../../components/ui/FormDrawer';
import api from '../../services/api';
import { hasPermissionMatch } from '../../utils/permissions';

type Env = 'sandbox' | 'live';
type Field = { key: string; label: string; secret: boolean; required: boolean; set: boolean; preview: string; formatHint: string | null; pattern: string | null };
type SmsGatewayRow = {
  type: string;
  label: string;
  setUp: boolean;
  enabled: boolean;
  priority: number | null;
  environment: Env;
  complete: boolean;
  issues: string[];
  fields: Field[];
  verification: { status: 'verified' | 'failed' | 'unverified'; message: string; at: string | null };
  readiness: { state: string; label: string; detail: string };
  routing: { role: 'primary' | 'failover' | 'simulated' | null; order: number | null };
  toggleLock: string | null;
};
type OtpMode = 'sms' | 'sandbox' | 'dev' | 'unavailable';
type SmsView = {
  gateways: SmsGatewayRow[];
  otp: { mode: OtpMode; route: string[]; message: string; devOtp: string | null; devOtpAllowed: boolean };
  priorityRange: { min: number; max: number };
};
type TestResult = { sent: boolean; simulated?: boolean; devMode?: boolean; provider?: string; code: string; message: string };

const READINESS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  verified: 'success',
  configured: 'warning',
  failed: 'error',
  incomplete: 'error',
  not_configured: 'default',
};

const OTP_BANNER: Record<OtpMode, { severity: 'success' | 'warning' | 'error' | 'info'; title: string }> = {
  sms: { severity: 'success', title: 'Live SMS active' },
  sandbox: { severity: 'warning', title: 'Sandbox — SMS simulated' },
  dev: { severity: 'warning', title: 'Dev OTP mode active' },
  unavailable: { severity: 'error', title: 'SMS unavailable' },
};

const E164 = /^\+[1-9]\d{7,14}$/;

const apiMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || (err as Error)?.message || fallback;

function phoneError(raw: string): string | undefined {
  const phone = raw.replace(/[\s-]/g, '');
  if (!phone) return undefined;
  if (!phone.startsWith('+')) return 'Start with + and the country code, e.g. +91';
  if (!/^\+\d+$/.test(phone)) return 'Digits only after the +';
  if (!E164.test(phone)) return 'Country code + number must be 8–15 digits';
  if (phone.startsWith('+91') && !/^\+91[6-9]\d{9}$/.test(phone)) return 'Indian numbers need 10 digits starting with 6–9 after +91';
  return undefined;
}

function ConfigureDrawer({ open, row, view, onClose, onSaved }: {
  open: boolean;
  row: SmsGatewayRow | null;
  view: SmsView | undefined;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const queryClient = useQueryClient();
  const [env, setEnv] = useState<Env>('sandbox');
  const [priority, setPriority] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!open || !row) return;
    setEnv(row.environment);
    setPriority(row.priority != null ? String(row.priority) : '');
    setEnabled(row.enabled);
    setValues({});
    setReveal({});
    setVerifyResult(null);
  }, [open, row]);

  const fieldErrors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of row?.fields || []) {
      const v = (values[f.key] || '').trim();
      if (!v) { if (f.required && !f.set) out[f.key] = `${f.label} is required`; continue; }
      if (f.pattern && !new RegExp(f.pattern).test(v)) out[f.key] = `Invalid format — ${f.formatHint}`;
    }
    return out;
  }, [values, row]);

  const errors = useMemo(() => {
    const list: string[] = [];
    const p = Number(priority);
    const range = view?.priorityRange || { min: 1, max: 99 };
    if (!priority) list.push('Enter a priority');
    else if (!Number.isInteger(p) || p < range.min || p > range.max) list.push(`Priority must be ${range.min}–${range.max}`);
    else if (enabled) {
      const clash = view?.gateways.find((g) => g.type !== row?.type && g.enabled && g.priority === p);
      if (clash) list.push(`Priority ${p} is already used by ${clash.label}`);
    }
    const credErrors = Object.values(fieldErrors);
    if (enabled) list.push(...credErrors);
    else list.push(...Object.entries(fieldErrors).filter(([k]) => (values[k] || '').trim()).map(([, m]) => m));
    return list;
  }, [priority, enabled, fieldErrors, values, view, row]);

  const credentialsComplete = Object.keys(fieldErrors).length === 0;

  useEffect(() => { if (enabled && !credentialsComplete) setEnabled(false); }, [enabled, credentialsComplete]);

  const typed = () => Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));

  const saveMut = useMutation({
    mutationFn: async () => api.put(`/sms/gateways/${row!.type}`, { environment: env, priority: Number(priority), enabled, credentials: typed() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sms-gateways-admin'] });
      onSaved(`${row?.label} saved${enabled ? ' and enabled' : ''}`);
      onClose();
    },
  });

  const verifyMut = useMutation({
    mutationFn: async () => (await api.post(`/sms/gateways/${row!.type}/verify`, { credentials: typed() })).data.data as { ok: boolean; message: string },
    onSuccess: (r) => { setVerifyResult(r); queryClient.invalidateQueries({ queryKey: ['sms-gateways-admin'] }); },
    onError: (err) => setVerifyResult({ ok: false, message: apiMessage(err, 'Verification failed') }),
  });

  useEffect(() => { if (open) saveMut.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!row) return null;
  const taken = (view?.gateways || []).filter((g) => g.type !== row.type && g.enabled).map((g) => `${g.label} ${g.priority}`).join(', ');

  return (
    <FormDrawer
      open={open}
      title={`Configure ${row.label}`}
      onClose={onClose}
      onSubmit={() => { if (!errors.length) saveMut.mutate(); }}
      submitLabel="Save"
      submitHint={errors.length ? `Save is blocked — ${errors.length} issue${errors.length === 1 ? '' : 's'} listed above` : undefined}
      disableSubmit={errors.length > 0}
      loading={saveMut.isPending}
      width={580}
    >
      <Stack spacing={2.25}>
        {saveMut.isError && <Alert severity="error">{apiMessage(saveMut.error, 'Could not save')}</Alert>}

        <Box>
          <Typography variant="body2" fontWeight={650} sx={{ mb: 0.75 }}>Environment *</Typography>
          <ToggleButtonGroup exclusive size="small" value={env} onChange={(_, v: Env | null) => v && setEnv(v)}>
            <ToggleButton value="sandbox" sx={{ textTransform: 'none', px: 2 }}>Sandbox (simulated)</ToggleButton>
            <ToggleButton value="live" sx={{ textTransform: 'none', px: 2 }}>Live (real SMS)</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {env === 'live'
              ? 'Messages are delivered through the provider and OTPs become random codes. Enabling runs a credential check first.'
              : `Messages are only logged, never delivered, and every OTP stays ${view?.otp.devOtp || 'the dev OTP'}. Use this while testing.`}
          </Typography>
        </Box>

        <TextField
          required
          fullWidth
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value.replace(/[^\d]/g, '').slice(0, 2))}
          inputProps={{ inputMode: 'numeric' }}
          helperText={`Lower is tried first; the next gateway is the failover. Unique among enabled gateways.${taken ? ` In use: ${taken}.` : ''}`}
        />

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="body2" fontWeight={650} sx={{ mb: 0.5 }}>Credentials</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Saved values are never shown — leave a field blank to keep it.
          </Typography>
          <Stack spacing={1.75}>
            {row.fields.map((f) => {
              const err = (values[f.key] || '').trim() || enabled ? fieldErrors[f.key] : undefined;
              return (
                <TextField
                  key={f.key}
                  fullWidth
                  required={f.required}
                  label={f.label}
                  autoComplete="off"
                  type={f.secret && !reveal[f.key] ? 'password' : 'text'}
                  value={values[f.key] || ''}
                  onChange={(e) => { setValues({ ...values, [f.key]: e.target.value }); setVerifyResult(null); }}
                  placeholder={f.set ? 'Leave blank to keep saved value' : f.formatHint || undefined}
                  error={!!err}
                  helperText={err || (f.set ? `Saved: ${f.preview}` : f.required ? `Required · ${f.formatHint}` : `Optional · ${f.formatHint}`)}
                  InputProps={f.secret ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" edge="end" onClick={() => setReveal((r) => ({ ...r, [f.key]: !r[f.key] }))}>
                          {reveal[f.key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  } : undefined}
                />
              );
            })}
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NetworkCheck />}
                disabled={!credentialsComplete || verifyMut.isPending}
                onClick={() => verifyMut.mutate()}
                sx={{ textTransform: 'none', fontWeight: 650 }}
              >
                {verifyMut.isPending ? 'Checking…' : 'Verify credentials'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {credentialsComplete ? 'Asks the provider to confirm the keys — no SMS is sent.' : 'Fill all required fields to verify.'}
              </Typography>
            </Stack>
            {verifyResult && <Alert severity={verifyResult.ok ? 'success' : 'error'}>{verifyResult.message}</Alert>}
          </Stack>
        </Box>

        <Box>
          <FormControlLabel
            control={<Switch checked={enabled} disabled={!credentialsComplete} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Enabled"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
            {!credentialsComplete
              ? 'Complete the required credentials before enabling.'
              : env === 'live'
                ? 'Saving with Enabled on checks the credentials with the provider first.'
                : 'Sandbox gateways simulate SMS only.'}
          </Typography>
        </Box>

        {errors.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight={650}>Fix these before saving:</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>{errors.map((e) => <li key={e}><Typography variant="body2">{e}</Typography></li>)}</Box>
          </Alert>
        )}
      </Stack>
    </FormDrawer>
  );
}

export default function SmsGatewaysPage() {
  const queryClient = useQueryClient();
  const permissions = useSelector((s: { auth: { permissions: string[] } }) => s.auth.permissions);
  const userType = useSelector((s: { auth: { user: { userType?: string } | null } }) => s.auth.user?.userType);
  const canManage = userType === 'super_admin'
    || hasPermissionMatch(permissions, 'settings.settings.update')
    || hasPermissionMatch(permissions, 'settings.settings.edit');

  const [configRow, setConfigRow] = useState<SmsGatewayRow | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<SmsGatewayRow | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testGateway, setTestGateway] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const { data: view, isLoading } = useQuery({
    queryKey: ['sms-gateways-admin'],
    queryFn: async () => (await api.get('/sms/gateways')).data.data as SmsView,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['sms-gateways-admin'] });

  const toggleMut = useMutation({
    mutationFn: async ({ row, enabled }: { row: SmsGatewayRow; enabled: boolean }) => api.put(`/sms/gateways/${row.type}`, { enabled }),
    onSuccess: (_, { row, enabled }) => { refresh(); setToast({ message: `${row.label} ${enabled ? 'enabled' : 'disabled'}`, severity: 'success' }); },
    onError: (err) => setToast({ message: apiMessage(err, 'Could not update gateway'), severity: 'error' }),
  });

  const verifyMut = useMutation({
    mutationFn: async (row: SmsGatewayRow) => (await api.post(`/sms/gateways/${row.type}/verify`, {})).data.data as { ok: boolean; message: string },
    onSuccess: (r) => { refresh(); setToast({ message: r.message, severity: r.ok ? 'success' : 'error' }); },
    onError: (err) => setToast({ message: apiMessage(err, 'Verification failed'), severity: 'error' }),
  });

  const testMut = useMutation({
    mutationFn: async () => (await api.post('/sms/test', { phone: testPhone.replace(/[\s-]/g, ''), gateway: testGateway || undefined })).data.data as TestResult,
    onSuccess: (r) => setTestResult(r),
    onError: (err) => setTestResult({ sent: false, code: '', message: apiMessage(err, 'Test SMS failed') }),
  });

  const rows = view?.gateways || [];
  const configured = rows.filter((g) => g.complete);
  const selectedGateway = rows.find((g) => g.type === testGateway);
  const phoneErr = phoneError(testPhone);
  const testBlocker = !testPhone.trim()
    ? 'Enter a phone number with country code'
    : phoneErr
      ? phoneErr
      : testGateway
        ? (selectedGateway?.complete ? null : `${selectedGateway?.label || 'This gateway'} is not configured yet`)
        : (view?.otp.mode === 'sms' ? null : 'No Live gateway is enabled — pick a configured gateway to test, or enable one in Live');
  const testNote = testGateway
    ? (selectedGateway?.environment === 'sandbox' ? `${selectedGateway.label} is in Sandbox, so the SMS will be simulated, not delivered.` : `Sends through ${selectedGateway?.label} even if it is disabled.`)
    : 'Sends exactly as an OTP would: primary gateway first, failover if it fails.';

  const banner = view ? OTP_BANNER[view.otp.mode] : null;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>SMS Gateways</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Twilio and MSG91 deliver OTP and transactional SMS. Lower priority is tried first; the next enabled Live gateway is the failover.
        Priority is ignored while a gateway is disabled.
      </Typography>

      {view && banner && (
        <Alert severity={banner.severity} sx={{ mb: 2 }}>
          <strong>{banner.title}:</strong> {view.otp.message}
          {view.otp.mode !== 'sms' && view.otp.mode !== 'unavailable' && ' Enable a gateway in Live to send real OTPs.'}
        </Alert>
      )}

      {isLoading && <Typography>Loading...</Typography>}

      <Stack spacing={1}>
        {rows.map((row) => {
          const role = row.routing.role === 'primary' ? 'Primary' : row.routing.role === 'failover' ? `Failover #${(row.routing.order || 2) - 1}` : row.routing.role === 'simulated' ? 'Simulated route' : null;
          const isLastLive = view?.otp.mode === 'sms' && view.otp.route.length === 1 && view.otp.route[0] === row.type;
          return (
            <Box key={row.type} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={700}>{row.label}</Typography>
                    {role && <Chip size="small" color="primary" label={role} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />}
                  </Stack>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={row.enabled ? 'Enabled' : 'Disabled'} color={row.enabled ? 'success' : 'default'} sx={{ fontWeight: 650 }} />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={row.environment === 'live' ? 'error' : 'warning'}
                      label={row.environment === 'live' ? 'Environment: Live' : 'Environment: Sandbox (simulated)'}
                    />
                    <Tooltip title={row.readiness.detail}>
                      <Chip size="small" variant="outlined" color={READINESS_COLOR[row.readiness.state] || 'default'} label={row.readiness.label} />
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {row.priority != null ? (row.enabled ? `Priority ${row.priority}` : `Priority ${row.priority} (ignored while disabled)`) : 'Priority —'}
                  </Typography>
                  <Stack direction="row" spacing={1.25} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {row.fields.map((f) => (
                      <Stack key={f.key} direction="row" spacing={0.4} alignItems="center">
                        {f.set
                          ? <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                          : <RadioButtonUnchecked sx={{ fontSize: 14, color: f.required ? 'error.main' : 'text.disabled' }} />}
                        <Typography variant="caption" color={f.set ? 'text.primary' : 'text.secondary'}>
                          {f.label}{f.required ? '' : ' (optional)'}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                {canManage && (
                  <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                    <Button size="small" variant="outlined" onClick={() => setConfigRow(row)}>Configure</Button>
                    <Tooltip title={row.complete ? 'Verify credentials with the provider (no SMS sent)' : 'Complete the credentials first'}>
                      <span>
                        <IconButton size="small" disabled={!row.complete || verifyMut.isPending} onClick={() => verifyMut.mutate(row)}>
                          <NetworkCheck fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={row.toggleLock || (row.enabled ? 'Disable' : 'Enable')}>
                      <span>
                        <Switch
                          checked={row.enabled}
                          disabled={!!row.toggleLock || toggleMut.isPending}
                          onChange={(e) => {
                            if (!e.target.checked && isLastLive) setConfirmDisable(row);
                            else toggleMut.mutate({ row, enabled: e.target.checked });
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Box mt={3} p={2} bgcolor="background.paper" borderRadius={2} sx={{ border: 1, borderColor: 'divider' }}>
        <Typography fontWeight={700} gutterBottom>Test SMS</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Sends a sample OTP to your phone so you can confirm delivery. The code is shown below to compare with the SMS you receive.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small"
            label="Phone"
            value={testPhone}
            onChange={(e) => { setTestPhone(e.target.value.replace(/[^\d+\s-]/g, '').slice(0, 18)); setTestResult(null); }}
            placeholder="+919876543210"
            error={!!phoneErr}
            helperText={phoneErr || 'Country code + number, e.g. +919876543210'}
            sx={{ minWidth: 240 }}
          />
          <TextField
            select
            size="small"
            label="Send via"
            value={testGateway}
            onChange={(e) => { setTestGateway(e.target.value); setTestResult(null); }}
            sx={{ minWidth: 240 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            helperText={testNote}
          >
            <MenuItem value="">Active OTP route</MenuItem>
            {rows.map((g) => (
              <MenuItem key={g.type} value={g.type} disabled={!g.complete}>
                {g.label}{g.complete ? ` · ${g.environment === 'live' ? 'Live' : 'Sandbox'}` : ' · not configured'}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={testBlocker || ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => testMut.mutate()}
                disabled={!canManage || !!testBlocker || testMut.isPending}
                sx={{ height: 40 }}
              >
                {testMut.isPending ? 'Sending…' : 'Send Test'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
        {testBlocker && testPhone.trim() === '' && configured.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            No gateway is configured yet — use Configure on Twilio or MSG91 first.
          </Typography>
        )}
        {testBlocker && testPhone.trim() !== '' && !phoneErr && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>{testBlocker}</Typography>
        )}
        {testResult && (
          <Alert severity={testResult.sent ? 'success' : testResult.simulated || testResult.devMode ? 'warning' : 'error'} sx={{ mt: 1.5 }}>
            {testResult.message}
            {testResult.code && !testResult.sent && ` Sample code: ${testResult.code}.`}
          </Alert>
        )}
      </Box>

      <ConfigureDrawer
        open={!!configRow}
        row={configRow}
        view={view}
        onClose={() => setConfigRow(null)}
        onSaved={(message) => setToast({ message, severity: 'success' })}
      />

      <Dialog open={!!confirmDisable} onClose={() => setConfirmDisable(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable {confirmDisable?.label}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            It is the only Live SMS gateway. After disabling,{' '}
            {view?.otp.devOtpAllowed
              ? <>SMS stops being delivered and <strong>Dev OTP mode</strong> starts — every OTP becomes the fixed dev code.</>
              : <>OTP requests will <strong>fail</strong> because Dev OTP is not allowed in production.</>}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            if (confirmDisable) toggleMut.mutate({ row: confirmDisable, enabled: false });
            setConfirmDisable(null);
          }}>Disable</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
